import Hls from "hls.js";

export const FRAME_WIDTH = 1920;
export const FRAME_HEIGHT = 1080;
export const IS_LOW_QUALITY = true;
export const IS_LOW_QUALITY_OCR = false;

export const extractFrameFromHLSStream = ({
  videoUrl,
  targetTime,
  enableLogging = false,
  lowResolution = IS_LOW_QUALITY_OCR,
}) => {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.crossOrigin = "anonymous";
    video.muted = true;
    video.preload = "metadata";
    video.playsInline = true;

    let frameWidth = FRAME_WIDTH;
    let frameHeight = FRAME_HEIGHT;

    let hls = null;
    let cleanedUp = false;

    const cleanup = () => {
      if (cleanedUp) return;
      cleanedUp = true;

      video.removeEventListener("error", onVideoError);
      video.removeEventListener("loadedmetadata", onLoadedMetadata);
      video.removeEventListener("seeked", onSeeked);
      video.removeEventListener("canplay", onCanPlay);

      if (hls) {
        try {
          hls.destroy();
        } catch (e) {
          if (enableLogging) console.warn("Error destroying HLS instance:", e);
        }
        hls = null;
      }

      if (video.parentNode) {
        video.parentNode.removeChild(video);
      }
    };

    const onVideoError = (error) => {
      cleanup();
      reject(new Error(`Video error: ${error?.message || "Unknown video error"}`));
    };

    const onHLSError = (_event, data) => {
      if (enableLogging) console.error("HLS Error:", data);

      if (data.fatal) {
        cleanup();
        switch (data.type) {
          case Hls.ErrorTypes.NETWORK_ERROR:
            reject(new Error(`Network error loading HLS stream: ${data.details}`));
            break;
          case Hls.ErrorTypes.MEDIA_ERROR:
            reject(new Error(`Media error loading HLS stream: ${data.details}`));
            break;
          default:
            reject(new Error(`Fatal HLS error: ${data.details}`));
            break;
        }
      }
    };

    let metadataLoaded = false;
    let seekCompleted = false;

    const onLoadedMetadata = () => {
      metadataLoaded = true;
      if (enableLogging) {
        console.log(`Video metadata loaded. Duration: ${video.duration}s, seeking to: ${targetTime}s`);
      }

      try {
        const seekTime = Math.max(0, Math.min(targetTime, video.duration));
        video.currentTime = seekTime;
      } catch (error) {
        cleanup();
        reject(new Error(`Failed to seek video: ${error.message}`));
      }
    };

    const onCanPlay = () => {
      if (enableLogging) console.log("Video can play, current time:", video.currentTime);
      if (metadataLoaded && !seekCompleted && Math.abs(video.currentTime - targetTime) < 1) {
        onSeeked();
      }
    };

    const onSeeked = () => {
      if (seekCompleted) return;
      seekCompleted = true;

      if (enableLogging) console.log("Video seeked to:", video.currentTime);

      try {
        const canvas = document.createElement("canvas");
        canvas.width = frameWidth;
        canvas.height = frameHeight;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        cleanup();
        resolve(canvas);
      } catch (error) {
        cleanup();
        reject(new Error(`Failed to draw frame: ${error.message}`));
      }
    };

    video.addEventListener("error", onVideoError);
    video.addEventListener("loadedmetadata", onLoadedMetadata);
    video.addEventListener("seeked", onSeeked);
    video.addEventListener("canplay", onCanPlay);

    // Optimize URL for faster loading
    let optimizedUrl = videoUrl;

    if (lowResolution) {
      if (videoUrl.includes("master_highest.m3u8")) {
        optimizedUrl = videoUrl.replace("master_highest.m3u8", "master_lowest.m3u8");
        if (enableLogging) console.log("Using optimized (lowest quality) stream");
      } else if (videoUrl.includes("index.m3u8")) {
        optimizedUrl = videoUrl.replace("index.m3u8", "index_360p.m3u8");
        if (enableLogging) console.log("Using optimized (360p) stream");
      }
    } else {
      optimizedUrl = videoUrl.replace("master_lowest.m3u8", "master_highest.m3u8");
    }

    // Initialize HLS
    if (Hls.isSupported()) {
      if (enableLogging) console.log("Initializing HLS for stream:", optimizedUrl);

      try {
        hls = new Hls({
          enableWorker: false,
          maxBufferLength: 5,
          maxMaxBufferLength: 10,
          maxBufferSize: 10 * 1000 * 1000,
          maxBufferHole: 0.5,
          lowLatencyMode: false,
          backBufferLength: 0,
          manifestLoadingTimeOut: 10000,
          manifestLoadingMaxRetry: 2,
          manifestLoadingRetryDelay: 500,
          levelLoadingTimeOut: 10000,
          fragLoadingTimeOut: 10000,
          startPosition: Math.max(0, targetTime),
        });

        hls.on(Hls.Events.ERROR, onHLSError);

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          if (enableLogging) console.log("HLS manifest parsed successfully");
          if (hls.levels.length > 0) {
            hls.currentLevel = hls.levels.length - 1;
            if (enableLogging) console.log(`Selected quality level: ${hls.levels.length - 1} (lowest)`);
          }
        });

        hls.loadSource(optimizedUrl);
        hls.attachMedia(video);
      } catch (error) {
        cleanup();
        reject(new Error(`Failed to initialize HLS: ${error.message}`));
      }
    } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
      if (enableLogging) console.log("Using native HLS support (Safari)");
      video.src = optimizedUrl;
    } else {
      cleanup();
      reject(new Error("HLS is not supported in this browser"));
    }

    setTimeout(() => {
      if (!seekCompleted) {
        cleanup();
        reject(new Error("Timeout: Failed to extract frame within 20 seconds"));
      }
    }, 20000);
  });
};

export const preprocessFrameGrayscale = (canvas) => {
  const ctx = canvas.getContext("2d", { willReadFrequently: true });

  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;
  const length = data.length;

  // Optimized grayscale conversion
  for (let i = 0; i < length; i += 4) {
    const gray = (data[i] * 77 + data[i + 1] * 151 + data[i + 2] * 28) >> 8;
    data[i] = gray;
    data[i + 1] = gray;
    data[i + 2] = gray;
  }

  ctx.putImageData(imageData, 0, 0);

  return canvas;
};

export const preprocessFrameBinaryThreshold = (canvas, coordinates) => {
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  const threshold = 120;

  coordinates.forEach((coordinate) => {
    const { x, y, width, height } = coordinate;

    const imageData = ctx.getImageData(x, y, width, height);
    const data = imageData.data; // This is a Uint8ClampedArray: [R, G, B, A, ...]

    // Loop through every pixel in the rectangle's data (4 values at a time).
    for (let i = 0; i < data.length; i += 4) {
      // Since the image is already grayscaled, R, G, and B values are the same.
      // We can just read one of them for the comparison.
      const grayValue = data[i];

      // Apply the binary threshold.
      // If the gray value is above the threshold, set the pixel to white, otherwise black.
      const colorValue = grayValue > threshold ? 255 : 0;

      data[i] = colorValue; // Red channel
      data[i + 1] = colorValue; // Green channel
      data[i + 2] = colorValue; // Blue channel
      // The alpha channel (data[i + 3]) is left unchanged.
    }

    ctx.putImageData(imageData, x, y);
  });

  return canvas;
};

export const drawRectanglesOnGrayscale = (grayscaleCanvas, coordinates) => {
  const canvas = document.createElement("canvas");
  canvas.width = grayscaleCanvas.width;
  canvas.height = grayscaleCanvas.height;

  const ctx = canvas.getContext("2d");
  ctx.drawImage(grayscaleCanvas, 0, 0);

  // Draw rectangles on the canvas
  ctx.strokeStyle = "#FF0000"; // Red color for rectangles
  ctx.lineWidth = 2;

  coordinates.forEach((box, index) => {
    const rectangle = {
      left: box.x,
      top: box.y,
      width: box.width,
      height: box.height,
    };

    ctx.strokeRect(rectangle.left, rectangle.top, rectangle.width, rectangle.height);

    // Add label with coordinate index
    ctx.fillStyle = "#FF0000";
    ctx.font = "16px Arial";
    ctx.fillText(`${index + 1}`, rectangle.left + 2, rectangle.top + 16);
  });

  return canvas;
};

export const preprocessCroppedFrameBinaryThreshold = (croppedCanvas) => {
  const ctx = croppedCanvas.getContext("2d", { willReadFrequently: true });
  const threshold = 128;

  const imageData = ctx.getImageData(0, 0, croppedCanvas.width, croppedCanvas.height);
  const data = imageData.data; // This is a Uint8ClampedArray: [R, G, B, A, ...]

  // Loop through every pixel in the rectangle's data (4 values at a time).
  for (let i = 0; i < data.length; i += 4) {
    // Since the image is already grayscaled, R, G, and B values are the same.
    // We can just read one of them for the comparison.
    const grayValue = data[i];

    // Apply the binary threshold.
    // If the gray value is above the threshold, set the pixel to white, otherwise black.
    const colorValue = grayValue > threshold ? 255 : 0;

    data[i] = colorValue; // Red channel
    data[i + 1] = colorValue; // Green channel
    data[i + 2] = colorValue; // Blue channel
    // The alpha channel (data[i + 3]) is left unchanged.
  }

  ctx.putImageData(imageData, 0, 0);

  return croppedCanvas;
};

export const getCroppedFrame = (canvas, coordinate) => {
  const { x, y, width, height } = coordinate;
  let croppedCanvas = document.createElement("canvas");

  croppedCanvas.width = width;
  croppedCanvas.height = height;

  const croppedCtx = croppedCanvas.getContext("2d");

  croppedCtx.drawImage(canvas, x, y, width, height, 0, 0, width, height);

  //   croppedCanvas = preprocessFrameGrayscale(croppedCanvas);
  //   croppedCanvas = preprocessCroppedFrameBinaryThreshold(croppedCanvas);

  return croppedCanvas;
};
